import styled, { ThemeProvider, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Wrapper, Content } from 'ui/sidebar';
import Breadcrumb from 'components/Breadcrumb';
import {
  PageHeader,
  size,
  spacing,
  ui,
  Container,
  Media,
  MediaBody,
  MediaImage,
  ButtonNew,
  FullCard as RawFullCard,
  media,
} from 'ui';
import { MdCall } from 'react-icons/md';
import caretaker1 from 'images/caretaker-1.svg';
import caretaker2 from 'images/caretaker-2.svg';
import caretaker3 from 'images/caretaker-3.svg';
import sergicPapernest from 'images/sergic-papernest.svg';
import papernest from 'images/papernest.jpg';
import { careTakerTheme } from 'ui/colors';
import { CardElementContent } from '@tymate/sergic';

const cards = [
  {
    title: 'energyCard.title',
    imageUrl: caretaker1,
    description: 'energyCard.description',
    callToAction: 'energyCard.callToAction',
    link: 'energyCard.link',
  },
  {
    title: 'communicationCard.title',
    imageUrl: caretaker2,
    description: 'communicationCard.description',
    callToAction: 'communicationCard.callToAction',
    link: 'communicationCard.link',
  },
  {
    title: 'insuranceCard.title',
    imageUrl: caretaker3,
    description: 'insuranceCard.description',
    callToAction: 'insuranceCard.callToAction',
    link: 'insuranceCard.link',
  },
];

const PhoneCard = styled.a`
  display: inline-block;
  padding: ${spacing()};
  border: 1px solid ${ui('separator')};
  background-color: ${({ theme }) => theme.primary};
  color: #fff;
  font-size: ${size(2)};
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;

  &:hover {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.12);
  }
`;

const MainMedia = styled(Media)`
  flex-direction: column;

  ${media.medium`
    flex-direction: row;
  `}
`;

const MainMediaImage = styled(MediaImage)`
  margin-left: ${({ theme }) => theme.spacing(-2)};
  margin-right: ${({ theme }) => theme.spacing(-2)};
  margin-top: ${({ theme }) => theme.spacing(-2)};
  width: calc(100% + ${({ theme }) => theme.spacing(4)});
  margin-bottom: ${({ theme }) => theme.spacing()};

  ${media.tablet`
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    margin-top: 0;
  `}

  ${media.medium`
    width: 360px;
    max-width: 50%;
  `}
`;

const PapernestImage = styled.img`
  display: block;
  max-width: 100%;
`;

const TitleImage = styled.img`
  display: block;
  width: 400px;
  max-width: 100%;
  height: auto;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const PhoneCardLegend = styled.div`
  display: block;
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const Tip = styled.div`
  color: ${ui('textLight')};
  font-size: ${size(-1)};
  padding: ${spacing(0.5)} 0;
`;

const Img = styled.img`
  border-radius: 6px;
  width: 54px;
  height: 54px;

  ${media.tablet`
    width: 84px;
    height: 84px;
  `}
`;

const CardSubtitle = styled.div`
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const CardBody = styled.div`
  > * + * {
    margin-top: ${({ theme }) => theme.spacing()};
  }

  ${media.tablet`
    display: flex;
    align-items: stretch;
    height: 100%;

    > * {
      flex: 1;
    }

    > * + * {
      flex: 0;
      margin-top: 0;
      margin-left: ${({ theme }) => theme.spacing()};
    }
  `}
`;

const CardAction = styled.div`
  white-space: nowrap;
  display: flex;

  ${media.tablet`
    align-items: center;
  `}
`;

const Description = styled.p`
  margin-top: 0;
`;

const FullCard = styled(RawFullCard)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  ${props =>
    props.variant === 'main' &&
    css`
      margin-bottom: ${({ theme }) => theme.spacing(3)};
    `}
`;

const CaretakerLodge = () => {
  const { t } = useTranslation('caretakerLodge');

  return (
    <ThemeProvider theme={careTakerTheme}>
      <Wrapper>
        <Content>
          <PageHeader>
            <Breadcrumb />
          </PageHeader>

          <Container leftAligned medium>
            <FullCard variant="main">
              <CardElementContent hasEqualPadding>
                <MainMedia>
                  <MainMediaImage>
                    <PapernestImage src={papernest} alt="" />
                  </MainMediaImage>

                  <MediaBody>
                    <TitleImage src={sergicPapernest} alt="" />

                    <Description>{t('description')}</Description>

                    <PhoneCard href={`tel:${t('phoneNumber')}`}>
                      <MdCall /> {t('formattedPhoneNumber')}
                    </PhoneCard>
                    <PhoneCardLegend>{t('phoneLegend')}</PhoneCardLegend>
                    <Tip>{t('phoneOpeningHours')}</Tip>
                  </MediaBody>
                </MainMedia>
              </CardElementContent>
            </FullCard>

            {cards.map(
              ({ title, description, imageUrl, callToAction, link }) => (
                <FullCard key={title}>
                  <CardElementContent style={{ padding: 8 }}>
                    <Media stretch>
                      <MediaImage>
                        <Img src={imageUrl} alt="" />
                      </MediaImage>

                      <MediaBody>
                        <CardBody>
                          <div>
                            <CardSubtitle>{t(title)}</CardSubtitle>

                            <p style={{ marginBottom: 0, marginTop: 4 }}>
                              {t(description)}
                            </p>
                          </div>

                          <CardAction>
                            <ButtonNew
                              isPrimary
                              as="a"
                              href={t(link)}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {t(callToAction)}
                            </ButtonNew>
                          </CardAction>
                        </CardBody>
                      </MediaBody>
                    </Media>
                  </CardElementContent>
                </FullCard>
              ),
            )}
          </Container>
        </Content>
      </Wrapper>
    </ThemeProvider>
  );
};

export default CaretakerLodge;
